import {Box, Occupancy} from "@variocube/app-api";
import {
    Avatar,
    Badge,
    LockStatusBadge,
    PushButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    useBoxes,
    useBoxLockStatus,
    useOccupancies,
    useStorageItemValue
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useMemo} from "react";
import {ClesyBoxType, CubeOrder, OrderStorage} from "../model";
import {CustomerCell, OrderCell} from "./common";
import {useLocalization} from "../../i18n";

interface BoxTableProps {
    onOpen: (boxNumber: string) => void;
}

export function BoxTable({onOpen}: BoxTableProps) {
    const {t} = useLocalization();
    const allBoxes = useBoxes();
    const clesyBoxes = useMemo(() => allBoxes.filter(isClesyBoxType), [allBoxes]);
    const orders = useStorageItemValue<OrderStorage>("orders");
    const occupancies = useOccupancies();

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>{t("Box")}</TableCell>
                    <TableCell>{t("BoxType")}</TableCell>
                    <TableCell>{t("Occupied")}</TableCell>
                    <TableCell>{t("Order")}</TableCell>
                    <TableCell>{t("Customer")}</TableCell>
                    <TableCell>{t("Status")}</TableCell>
                    <TableCell>{" "}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {clesyBoxes && clesyBoxes.map(box => {
                    const occupancy = occupancies.filter(occupancy => occupancy.boxNumber == box.number).pop();
                    const order = orders && occupancy && orders[occupancy.accessCode];
                    return (
                        <BoxRow
                            key={box.number}
                            box={box}
                            occupancy={occupancy}
                            order={order}
                            onOpen={onOpen}
                        />
                    );
                })}
            </TableBody>
        </Table>
    );
}

function isClesyBoxType(box: Box) {
    return box.types.indexOf(ClesyBoxType.SuitBag) >= 0
        || box.types.indexOf(ClesyBoxType.WelcomePackage) >= 0
        || box.types.indexOf(ClesyBoxType.Storage) >= 0;
}


interface BoxRowProps {
    box: Box;
    occupancy?: Occupancy;
    order?: CubeOrder;
    onOpen: (boxNumber: string) => void;
}

function BoxRow({box, occupancy, order, onOpen}: BoxRowProps) {
    const {t} = useLocalization();
    const status = useBoxLockStatus(box.number);
    const handleOpen = useCallback(() => onOpen(box.number), [onOpen, box]);

    const getBoxTypeDisplay = useCallback((boxType: string) => {
        switch (boxType) {
            case ClesyBoxType.WelcomePackage:
                return t("BoxTypeWelcomePackage");
            case ClesyBoxType.SuitBag:
                return t("BoxTypeSuitBag");
            default:
                return boxType;
        }
    }, []);

    return (
        <TableRow>
            <TableCell>
                <Avatar size={1.5}>{box.number}</Avatar>
            </TableCell>
            <TableCell>
                {box.types.map(getBoxTypeDisplay).join(", ")}
            </TableCell>
            <TableCell>
                {occupancy && (
                    <Badge>{t("Occupied")}</Badge>
                )}
            </TableCell>
            <OrderCell order={order}/>
            <CustomerCell order={order}/>
            <TableCell>
                {status && (
                    <LockStatusBadge lockStatus={status} strings={t}/>
                )}
            </TableCell>
            <TableCell>
                <PushButton primary onClick={handleOpen}>
                    {t("Open")}
                </PushButton>
            </TableCell>
        </TableRow>
    );

}
